import { z } from 'zod';

// ============================================================================
// Price Calendar Response (P2-PRC-003)
// ============================================================================

export const PriceCalendarDaySchema = z.object({
  date: z.string(),
  dayOfWeek: z.number().int().min(0).max(6),
  multiplier: z.number(),
  finalPrice: z.number().int().nonnegative(),
  isLocked: z.boolean(),
  lockedAt: z.string().datetime().nullable(),
});

export type PriceCalendarDay = z.infer<typeof PriceCalendarDaySchema>;

export const PriceCalendarResponseSchema = z.object({
  month: z.string(),
  ticketTypeId: z.string(),
  days: z.array(PriceCalendarDaySchema),
  generatedAt: z.string().datetime(),
});

export type PriceCalendarResponse = z.infer<typeof PriceCalendarResponseSchema>;

// ============================================================================
// Query schemas for calendar endpoints
// ============================================================================

export const GetPriceCalendarQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/, 'month must be in YYYY-MM format'),
  ticketTypeId: z.string().min(1, 'ticketTypeId is required'),
});

export type GetPriceCalendarQuery = z.infer<typeof GetPriceCalendarQuerySchema>;

export const CalendarCheapestQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be in YYYY-MM-DD format'),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be in YYYY-MM-DD format'),
  limit: z.coerce.number().int().positive().max(50).default(5),
});

export type CalendarCheapestQuery = z.infer<typeof CalendarCheapestQuerySchema>;

// ============================================================================
// Response schemas for CalendarController endpoints
// ============================================================================

// GET /calendar/cheapest/:ticketTypeId
export const CheapestDaySchema = z.object({
  date: z.string(),
  price: z.number().int().nonnegative(),
  isLocked: z.boolean(),
});

export type CheapestDay = z.infer<typeof CheapestDaySchema>;

// GET /calendar/:yearMonth/range/:ticketTypeId
export const MonthPriceRangeResponseSchema = z.object({
  ticketTypeId: z.string(),
  month: z.string(),
  minPrice: z.number(),
  minDate: z.string(),
  maxPrice: z.number(),
  maxDate: z.string(),
  priceVariance: z.number(),
});

export type MonthPriceRangeResponse = z.infer<typeof MonthPriceRangeResponseSchema>;
